import * as React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DocumentForm } from '../components/DocumentForm';
import { Document, DocumentFormData } from '../types';
import { DocumentsService } from '../services/documents-service';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
// import mockDocuments from '../data/mock-documents'; // Will be replaced by API call
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

export function EditDocumentPage() {
  const { id } = useParams({ from: '/_authenticated/documents/edit/$id' });
  const [document, setDocument] = React.useState<Document | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    const fetchDocument = async () => {
      setIsLoading(true);
      try {
        const fetchedDocument = await DocumentsService.getDocumentById(id);
        setDocument(fetchedDocument);
      } catch (err: any) {
        console.error('Failed to fetch document:', err);
        toast({
          title: 'Error',
          description: err.message || 'Failed to load document. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [id]);

  const navigate = useNavigate();

  const handleSubmit = async (data: DocumentFormData) => {
    setIsSaving(true);
    
    try {
      await DocumentsService.updateDocument(id, data);
      toast({
        title: 'Success',
        description: 'Document updated successfully',
      });
      // Navigate to the document details page
      navigate({ to: `/documents/${id}` });
    } catch (err: any) {
      console.error('Failed to update document:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to update document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: `/documents/${id}` });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Document Not Found</AlertTitle>
        <AlertDescription>
          The document with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  // Convert Document to DocumentFormData
  const initialData: DocumentFormData = {
    title: document.title || '',
    description: document.description || '',
    document_type: document.document_type,
    original_date: document.original_date || '',
    original_date_precision: document.original_date_precision,
    date_start: document.date_start,
    date_end: document.date_end,
    date_type: document.date_type,
    location: document.location || '',
    creator: document.creator || '',
    source: document.source || '',
    archive_source_location: document.archive_source_location || '',
    historical_context: document.historical_context || '',
    visibility: document.visibility,
    publication_name: document.publication_name || '',
    page_number: document.page_number || '',
    section: document.section || '',
    language: document.language || '',
    entities: document.entities || [],
    tags: document.tags || [],
    categories: document.categories || [],
    preservation: {
      source_archive: document.preservation.source_archive || '',
      condition: document.preservation.condition,
      archivist_notes: document.preservation.archivist_notes || '',
      handling_restrictions: document.preservation.handling_restrictions || '',
    },
    relations: {
      related_articles: document.relations?.related_articles || [],
      event_id: document.relations?.event_id || '',
      person_id: document.relations?.person_id || [],
      advertiser_id: document.relations?.advertiser_id || '',
      collections: document.relations?.collections || [],
    },
    metadata: document.metadata || {},
  };

  // Extract uploaded file info if document has media
  const uploadedFile = document.media && document.media.storage_path ? {
    id: document.id,
    filename: document.media.storage_path.split('/').pop() || 'document',
    storage_path: document.media.storage_path,
    thumbnail_path: document.media.thumbnail_path,
    file_type: document.media.file_type,
    file_size: document.media.file_size,
    uploaded_at: new Date(document.audit.created_at * 1000).toISOString(),
  } : null;

  const handleFileUpload = async (file: File) => {
    try {
      const result = await DocumentsService.uploadFile(file);
      if (result.success) {
        toast({
          title: 'Success',
          description: 'File uploaded successfully',
        });
        return result;
      }
    } catch (error: any) {
      console.error('File upload failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload file. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Edit Document: ${document.title}`}
        description="Update document information and metadata."
      />
      
      <Card>
        <CardContent className="pt-6">
          <DocumentForm 
            documentId={id}
            initialData={initialData} 
            onSubmit={handleSubmit} 
            onCancel={handleCancel}
            onFileUpload={handleFileUpload}
            uploadedFile={uploadedFile}
            isLoading={isSaving} 
          />
        </CardContent>
      </Card>
    </div>
  );
}
